/**
 * 接口配置定义
 * 存储所有接口的文档、参数、示例等信息
 */

export const API_CONFIGS = {
  ssz_ai: {
    id: 'ssz_ai',
    name: 'SSZ AI 接口',
    description: '创建/继续执行计划，查询执行状态和结果',
    methods: {
      post: {
        method: 'POST',
        path: '/api/ssz_ai',
        description: '创建执行计划或继续执行下一个Worker（入参格式：{"message": "原ssz_ai接口数据的json字符串"}）',
        requestParams: [
          {
            name: 'message',
            type: 'string',
            required: true,
            description: '原ssz_ai接口数据的JSON字符串，包含以下字段（统一使用驼峰命名）',
            example: '{"filePath":"tests/20251203151946_first.txt","taskId":"DIAG_31_202508211143401","userId":31,"requestRound":1,"userGroupId":1,"schemeId":1,"modelId":3,"callbackUrl":""}'
          },
          {
            name: 'message内的字段说明',
            type: 'object',
            required: false,
            description: 'message字段内包含的参数（统一使用驼峰命名）',
            nestedParams: [
              {
                name: 'filePath',
                type: 'string',
                required: true,
                description: '文件路径（OSS路径，相对于 https://pt-chat-bot.oss-cn-shanghai.aliyuncs.com/）',
                example: 'tests/20251203151946_first.txt'
              },
              {
                name: 'taskId',
                type: 'string',
                required: true,
                description: '任务ID，用于标识同一个任务的多次请求',
                example: 'DIAG_31_202508211143401'
              },
              {
                name: 'userId',
                type: 'number',
                required: true,
                description: '用户ID',
                example: 31
              },
              {
                name: 'requestRound',
                type: 'number',
                required: false,
                description: '请求轮次（可选）',
                example: 1
              },
              {
                name: 'userGroupId',
                type: 'number',
                required: false,
                description: '用户组ID（可选，原user_group_id）',
                example: 1
              },
              {
                name: 'schemeId',
                type: 'number',
                required: false,
                description: '方案ID（可选，如果提供则使用该方案，否则使用默认方案，原scheme_id）',
                example: 1
              },
              {
                name: 'modelId',
                type: 'number',
                required: false,
                description: '模型ID（可选，如果提供则使用该模型，原model_id）',
                example: 3
              },
              {
                name: 'uid',
                type: 'string',
                required: false,
                description: 'UID（可选，用于匹配步骤，如果不提供则使用 userId 作为 uid）',
                example: 'TEST001'
              },
              {
                name: 'callbackUrl',
                type: 'string',
                required: false,
                description: '回调URL（可选，用于接收任务状态通知，原callback_url）',
                example: ''
              }
            ]
          }
        ],
        requestExample: {
          create: {
            description: '创建执行计划（第一次请求）',
            data: {
              message: '{"filePath":"tests/20251203151946_first.txt","taskId":"DIAG_31_202508211143401","userId":31,"requestRound":1,"userGroupId":1,"schemeId":1,"modelId":3}'
            },
            // 用于显示：message字段内的内容（方便用户编辑）
            messageContent: {
              filePath: 'tests/20251203151946_first.txt',
              taskId: 'DIAG_31_202508211143401',
              userId: 31,
              requestRound: 1,
              userGroupId: 1,
              schemeId: 1,
              modelId: 3
            }
          },
          continue: {
            description: '继续执行（后续请求，只传必填参数）',
            data: {
              message: '{"filePath":"tests/20251203151946_first.txt","taskId":"DIAG_31_202508211143401","userId":31}'
            },
            // 用于显示：message字段内的内容（方便用户编辑）
            messageContent: {
              filePath: 'tests/20251203151946_first.txt',
              taskId: 'DIAG_31_202508211143401',
              userId: 31
            }
          }
        },
        responseExample: {
          success: {
            status: 200,
            msg: '任务已接收，正在处理',
            taskId: 'DIAG_31_202508211143401'
          },
          error: {
            status: 400,
            msg: '缺少必需参数: message'
          }
        },
        errorCodes: [
          {
            code: 400,
            message: '参数错误',
            description: '缺少必需参数或参数格式错误',
            examples: [
              '缺少必需参数: taskId',
              '缺少必需参数: filePath',
              'AI调用流程不存在或已禁用: 1',
              '模型不存在或已禁用: 3'
            ]
          },
          {
            code: 404,
            message: '资源不存在',
            description: '请求的资源不存在',
            examples: [
              '文件不存在',
              'task_id不存在或已过期'
            ]
          },
          {
            code: 409,
            message: '冲突',
            description: '请求与当前状态冲突',
            examples: [
              '该task_id正在执行中，请稍后重试'
            ]
          },
          {
            code: 500,
            message: '服务器错误',
            description: '服务器内部错误',
            examples: [
              '下载文件失败',
              '解析文件内容失败',
              '创建执行计划失败'
            ]
          }
        ]
      },
      get: {
        method: 'GET',
        path: '/api/ssz_ai',
        description: '查询执行状态和结果',
        queryParams: [
          {
            name: 'task_id',
            type: 'string',
            required: true,
            description: '任务ID',
            example: 'DIAG_31_20250821114340'
          }
        ],
        requestExample: {
          url: '/api/ssz_ai?task_id=DIAG_31_20250821114340'
        },
        responseExample: {
          executing: {
            code: 200,
            data: {
              task_id: 'DIAG_31_20250821114340',
              status: 'executing',
              progress: {
                current_step: 1,
                total_steps: 3
              },
              step_results: [
                {
                  step_order: 1,
                  step_name: '读取数据流',
                  worker_id: 17,
                  status: 'success',
                  knowledge_count: 5,
                  response: { /* ... */ },
                  execution_time: 1234
                }
              ]
            }
          },
          completed: {
            code: 200,
            data: {
              task_id: 'DIAG_31_20250821114340',
              status: 'completed',
              final_result: {
                plan_id: 123,
                plan_code: 'PLAN20250101123',
                scheme_name: '标准诊断方案',
                step_results: [ /* ... */ ],
                summary: {
                  total_steps: 3,
                  completed_steps: 3,
                  failed_steps: 0,
                  total_workers: 5,
                  completed_workers: 5
                }
              },
              step_results: [ /* ... */ ],
              total_execution_time: 45678
            }
          }
        },
        errorCodes: [
          {
            code: 400,
            message: '参数错误',
            description: '缺少必需参数',
            examples: [
              '缺少task_id参数'
            ]
          },
          {
            code: 404,
            message: '资源不存在',
            description: '请求的资源不存在',
            examples: [
              'task_id不存在或已过期',
              '执行计划不存在'
            ]
          }
        ]
      }
    }
  }
  // 后续可以添加更多接口配置
};

/**
 * 获取接口配置
 */
export function getApiConfig(apiId) {
  return API_CONFIGS[apiId] || null;
}

/**
 * 获取所有接口列表
 */
export function getAllApis() {
  return Object.values(API_CONFIGS).map(api => ({
    id: api.id,
    name: api.name,
    description: api.description,
    methods: Object.keys(api.methods || {})
  }));
}

